import { useId, type CSSProperties } from 'react';

/*
 * PlanWise mark as an inline SVG, plus the two loaders built on it.
 *
 * The mark is three stacked sheets (back, mid, front). Each sheet can be wrapped
 * in a soft diagonal mask; sliding that mask along the sheet's diagonal "draws"
 * or "fills" the sheet. Keyframes for the indeterminate loader live in
 * index.css (brand-wipe-*); the progress variant positions the masks inline.
 *
 * Geometry and colours are copied from public/favicon.svg (the logo mark); if the
 * mark changes, update both together.
 */

const SHAPE_PATH = 'M 130.28 37.00 L 194.20 37.00 A 14 14 0 0 1 204.10 41.10 L 278.77 115.77 A 18 18 0 0 1 278.77 141.23 L 143.10 276.90 A 14 14 0 0 1 133.20 281.00 L 54.00 281.00 A 17 17 0 0 1 37.00 264.00 L 37.00 223.89 C 37.00 223.56 37.00 223.22 37.01 222.89 C 37.02 222.56 37.04 222.22 37.06 221.89 C 37.08 221.56 37.10 221.22 37.13 220.89 C 37.16 220.56 37.20 220.22 37.24 219.89 C 37.28 219.56 37.32 219.22 37.38 218.89 C 37.43 218.56 37.48 218.22 37.54 217.89 C 37.60 217.56 37.67 217.22 37.74 216.89 C 37.81 216.56 37.89 216.22 37.97 215.89 C 38.05 215.56 38.14 215.22 38.23 214.89 C 38.32 214.56 38.42 214.22 38.53 213.89 C 38.63 213.56 38.74 213.22 38.86 212.89 C 38.97 212.56 39.09 212.22 39.22 211.89 C 39.35 211.56 39.48 211.22 39.62 210.89 C 39.76 210.56 39.91 210.22 40.06 209.89 C 40.22 209.56 40.38 209.22 40.54 208.89 C 40.71 208.56 40.89 208.22 41.07 207.89 C 41.25 207.56 41.44 207.22 41.63 206.89 C 41.83 206.56 42.03 206.22 42.25 205.89 C 42.46 205.56 42.68 205.22 42.91 204.89 C 43.14 204.56 43.38 204.22 43.62 203.89 C 43.87 203.56 44.13 203.22 44.40 202.89 C 44.67 202.56 44.94 202.22 45.23 201.89 C 45.52 201.56 45.83 201.22 46.14 200.89 C 46.46 200.56 46.82 200.22 47.12 199.89 C 47.43 199.56 47.74 199.22 47.98 198.89 C 48.22 198.56 48.45 198.22 48.55 197.89 C 48.65 197.56 48.67 197.22 48.59 196.89 C 48.51 196.56 48.30 196.22 48.05 195.89 C 47.81 195.56 47.46 195.22 47.14 194.89 C 46.81 194.56 46.43 194.22 46.08 193.89 C 45.73 193.56 45.37 193.22 45.03 192.89 C 44.69 192.56 44.35 192.22 44.04 191.89 C 43.72 191.56 43.42 191.22 43.14 190.89 C 42.85 190.56 42.57 190.22 42.31 189.89 C 42.05 189.56 41.81 189.22 41.57 188.89 C 41.34 188.56 41.11 188.22 40.90 187.89 C 40.69 187.56 40.49 187.22 40.30 186.89 C 40.11 186.56 39.93 186.22 39.76 185.89 C 39.59 185.56 39.43 185.22 39.27 184.89 C 39.12 184.56 38.98 184.22 38.84 183.89 C 38.71 183.56 38.58 183.22 38.46 182.89 C 38.34 182.56 38.23 182.22 38.13 181.89 C 38.02 181.56 37.93 181.22 37.84 180.89 C 37.75 180.56 37.67 180.22 37.59 179.89 C 37.52 179.56 37.45 179.22 37.39 178.89 C 37.33 178.56 37.28 178.22 37.23 177.89 C 37.19 177.56 37.15 177.22 37.12 176.89 C 37.08 176.56 37.06 176.29 37.04 175.89 C 37.02 175.49 37.01 174.87 37.00 174.47 L 37.00 130.28 A 20 20 0 0 1 42.86 116.14 L 116.14 42.86 A 20 20 0 0 1 130.28 37.00 Z';
const MID_AREA = 'M 420.00 95.00 L 138.63 95.00 A 16 16 0 0 0 127.31 99.69 L -90.00 317.00 L -90.00 430.00 L 420.00 430.00 L 420.00 95.00 Z';
const FRONT_AREA = 'M 420.00 150.00 L 173.63 150.00 A 16 16 0 0 0 162.31 154.69 L -90.00 407.00 L -90.00 430.00 L 420.00 430.00 L 420.00 150.00 Z';
const MID_RIM = 'M 420 95 L 138.63 95.00 A 16 16 0 0 0 127.31 99.69 L -292.26 519.26';
const FRONT_RIM = 'M 420 150 L 173.63 150.00 A 16 16 0 0 0 162.31 154.69 L -257.26 574.26';

const SHEETS = ['back', 'mid', 'front'] as const;
type Sheet = (typeof SHEETS)[number];

/*
 * Mask travel along the (1, -1) diagonal, in viewBox units. The mark spans
 * x - y in [-245, 163] and the mask ramp is 40 units wide, so these offsets
 * take a sheet from fully hidden to fully shown.
 */
const FILL_HIDDEN = -143;
const FILL_SHOWN = 82;

type Variant = 'static' | 'loading' | 'progress';

interface BrandMarkProps {
  size?: number;
  className?: string;
  variant?: Variant;
  /** 0–100, only used by the progress variant. */
  progress?: number;
}

function fillOffset(progress: number, sheetIndex: number) {
  const third = 100 / SHEETS.length;
  const local = Math.min(1, Math.max(0, (progress - sheetIndex * third) / third));
  return FILL_HIDDEN + local * (FILL_SHOWN - FILL_HIDDEN);
}

export function BrandMark({ size = 32, className = '', variant = 'static', progress = 0 }: BrandMarkProps) {
  // useId() returns ":r0:"-style ids, which are not valid inside url(#...).
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const id = (name: string) => `pw-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;
  const masked = variant !== 'static';

  const sheetMask = (sheet: Sheet) => (masked ? url(`reveal-${sheet}`) : undefined);

  return (
    <svg
      viewBox="0 0 328 328"
      width={size}
      height={size}
      className={`brand-mark brand-mark--${variant} shrink-0 ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={id('shape')}>
          <path d={SHAPE_PATH} />
        </clipPath>
        <clipPath id={id('mid-area')}>
          <path d={MID_AREA} />
        </clipPath>
        <clipPath id={id('front-area')}>
          <path d={FRONT_AREA} />
        </clipPath>
        <linearGradient id={id('back')} gradientUnits="userSpaceOnUse" x1="135.45" y1="164.31" x2="137.18" y2="44.80">
          <stop offset="0" stopColor="#2d81fe" />
          <stop offset="1" stopColor="#8c95fe" />
        </linearGradient>
        <linearGradient id={id('mid-lo')} gradientUnits="userSpaceOnUse" x1="48.98" y1="251.14" x2="12.58" y2="154.21">
          <stop offset="0" stopColor="#009bff" />
          <stop offset="1" stopColor="#4e8aff" />
        </linearGradient>
        <linearGradient id={id('mid-hi')} gradientUnits="userSpaceOnUse" x1="281.90" y1="163.68" x2="245.51" y2="66.76">
          <stop offset="0" stopColor="#14cac8" />
          <stop offset="1" stopColor="#67b9e0" />
        </linearGradient>
        <linearGradient id={id('mid-ramp')} gradientUnits="userSpaceOnUse" x1="30.78" y1="202.68" x2="263.70" y2="115.22">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#fff" stopOpacity="1" />
        </linearGradient>
        <linearGradient id={id('front')} gradientUnits="userSpaceOnUse" x1="70.69" y1="208.30" x2="231.76" y2="222.71">
          <stop offset="0" stopColor="#10cdc0" />
          <stop offset="1" stopColor="#26e5b4" />
        </linearGradient>
        <mask id={id('mid-mask')} maskUnits="userSpaceOnUse" x="0" y="0" width="328" height="328" style={{ maskType: 'alpha' }}>
          <rect x="0" y="0" width="328" height="328" fill={url('mid-ramp')} />
        </mask>
        {masked && (
          <>
            {/* Loading draws from the top-right corner; progress fills from the bottom-left. */}
            <linearGradient id={id('reveal-ramp')} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="20" y2="-20">
              <stop offset="0" stopColor="#fff" stopOpacity={variant === 'loading' ? 0 : 1} />
              <stop offset="1" stopColor="#fff" stopOpacity={variant === 'loading' ? 1 : 0} />
            </linearGradient>
            {SHEETS.map((sheet, i) => {
              const offset = variant === 'progress' ? fillOffset(progress, i) : 0;
              const style: CSSProperties | undefined =
                variant === 'progress' ? { transform: `translate(${offset}px, ${-offset}px)` } : undefined;
              return (
                <mask
                  key={sheet}
                  id={id(`reveal-${sheet}`)}
                  maskUnits="userSpaceOnUse"
                  x="-600"
                  y="-600"
                  width="1600"
                  height="1600"
                  style={{ maskType: 'alpha' }}
                >
                  <rect
                    className={`brand-reveal brand-reveal--${sheet}`}
                    x="-600"
                    y="-600"
                    width="1600"
                    height="1600"
                    fill={url('reveal-ramp')}
                    style={style}
                  />
                </mask>
              );
            })}
          </>
        )}
      </defs>
      <g clipPath={url('shape')}>
        {/* Track: a faint silhouette so an empty progress mark still reads as the logo. */}
        {variant === 'progress' && <path d={SHAPE_PATH} fill="currentColor" opacity="0.15" />}
        <g mask={sheetMask('back')}>
          <rect x="0" y="0" width="328" height="328" fill={url('back')} />
        </g>
        <g mask={sheetMask('mid')}>
          <path d={MID_AREA} fill={url('mid-lo')} />
          <path d={MID_AREA} fill={url('mid-hi')} mask={url('mid-mask')} />
          <path d={MID_RIM} fill="none" stroke="#69b2f0" strokeWidth="2.0" clipPath={url('mid-area')} />
        </g>
        <g mask={sheetMask('front')}>
          <path d={FRONT_AREA} fill={url('front')} />
          <path d={FRONT_RIM} fill="none" stroke="#44e1d1" strokeWidth="3.2" clipPath={url('front-area')} />
        </g>
      </g>
    </svg>
  );
}

interface BrandLoaderProps {
  size?: number;
  className?: string;
  /** Localized by the caller; announced to screen readers and optionally shown below the mark. */
  label: string;
  showLabel?: boolean;
}

/** Indeterminate loader: each sheet is drawn in diagonally, held, then erased. */
export function BrandLoader({ size = 40, className = '', label, showLabel = false }: BrandLoaderProps) {
  return (
    <div role="status" aria-live="polite" className={`inline-flex flex-col items-center gap-3 ${className}`}>
      <BrandMark size={size} variant="loading" />
      {showLabel ? (
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </div>
  );
}

interface BrandProgressProps {
  /** 0–100. */
  value: number;
  size?: number;
  className?: string;
  /** Text before the percentage, e.g. "Uploading...". */
  label?: string;
}

/** Determinate loader: each third of the progress fills one sheet, bottom-left first. */
export function BrandProgress({ value, size = 56, className = '', label }: BrandProgressProps) {
  const pct = Math.round(Math.min(100, Math.max(0, value)));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label}
      className={`flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400 ${className}`}
    >
      <BrandMark size={size} variant="progress" progress={pct} />
      <span className="text-xs tabular-nums">
        {label ? `${label} ` : ''}
        {pct}%
      </span>
    </div>
  );
}
